import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle, Search, Package, Loader2 } from 'lucide-react';
import { itemsAPI, scanAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export const ScanNotFoundSheet = ({ 
  open, 
  onClose, 
  scannedCode,
  onAssigned 
}) => {
  const { isOwner } = useAuth();
  const [mode, setMode] = useState('initial'); // 'initial' | 'search' | 'assigning'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (open) {
      setMode('initial');
      setSearchQuery('');
      setSearchResults([]);
      setSelectedItemId('');
    }
  }, [open]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const res = await itemsAPI.list({ search: searchQuery, page_size: 50 });
      // Handle paginated response format
      const data = res.data;
      setSearchResults(data.items || data);
      setMode('search');
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedItemId) {
      toast.error('Please select an item');
      return;
    }
    
    setAssigning(true);
    try {
      await scanAPI.assign({
        item_id: selectedItemId,
        code_value: scannedCode
      });
      toast.success('Code assigned to item');
      onAssigned?.();
      onClose();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to assign code';
      toast.error(message);
    } finally {
      setAssigning(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertCircle className="w-5 h-5" />
            Code Not Recognized
          </DialogTitle>
          <DialogDescription>
            The scanned code was not found in your inventory.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Display scanned code */}
          <div className="bg-muted rounded-lg p-3">
            <Label className="text-xs text-muted-foreground">Scanned Code</Label>
            <p className="font-mono text-sm mt-1 break-all">{scannedCode}</p>
          </div>

          {isOwner ? (
            <>
              {mode === 'initial' && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Would you like to assign this code to an existing item?
                  </p>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search for item..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <Button onClick={handleSearch} disabled={searching}>
                        {searching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {mode === 'search' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Select Item to Assign</Label>
                    <Button variant="ghost" size="sm" onClick={() => setMode('initial')}>
                      Back
                    </Button>
                  </div>
                  
                  {searchResults.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No items found</p>
                    </div>
                  ) : (
                    <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an item" />
                      </SelectTrigger>
                      <SelectContent>
                        {searchResults.map(item => (
                          <SelectItem key={item.id} value={item.id}>
                            <div className="flex flex-col">
                              <span>{item.name}</span>
                              {item.sku && (
                                <span className="text-xs text-muted-foreground">
                                  SKU: {item.sku}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {selectedItemId && (
                    <Button 
                      className="w-full" 
                      onClick={handleAssign}
                      disabled={assigning}
                    >
                      {assigning ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Assign Code to Item
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted-foreground">
                Please contact an owner to assign this code to an item.
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScanNotFoundSheet;
